/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useRef, useState } from "react";

import { useRouter } from "next/router";
import styled from "styled-components";

import { Layer } from "@vienna/common";

import { useCurrentUser } from "src/app/useCurrentUser";
import { BaseUser, CurrentUser } from "src/classes/user";
import { Avatar } from "src/components/Shared/User/Avatar";
import { Username } from "src/components/Shared/User/Username";
import { ButtonSmall, HighContrastButtonSmall } from "src/components/UI/Buttons/ButtonPrimitives";

interface Props {
  user: BaseUser | CurrentUser;
  children: React.ReactNode;
}

export const HoverCard = ({ user, children }: Props) => {
  const router = useRouter();
  const innerRef = useRef<HTMLDivElement>(null);
  const currentUser = useCurrentUser();

  const [hoverOpen, setHoverOpen] = useState<boolean>(false);
  const [timer, setTimer] = useState<any>(null);
  const dimensions = innerRef.current?.getBoundingClientRect();

  // current user might be undefined if logged out and on page
  const isSelf = user.snapshot.id === currentUser?.snapshot.id;
  const isFollowing = currentUser?.snapshot.following.some(
    (followed) => followed.snapshot.username === user.snapshot.username
  );

  const handleHoverOpen = () => {
    clearTimeout(timer);
    setTimer(
      setTimeout(() => {
        setHoverOpen(true);
      }, 400)
    );
  };

  const handleHoverClose = () => {
    clearTimeout(timer);
    setTimer(
      setTimeout(() => {
        setHoverOpen(false);
      }, 200)
    );
  };

  const handleFollow = () => {
    currentUser!.follow(user);
  };

  const handleUnfollow = () => {
    currentUser!.unfollow(user);
  };

  return (
    <div onMouseEnter={handleHoverOpen} onMouseLeave={handleHoverClose} style={{ position: "relative" }}>
      {hoverOpen && dimensions && (
        <StyledHoverCard style={{ top: dimensions.height + 5 }}>
          <div>
            <StyledHoverHeader>
              <Avatar size={40} src={user.avatarURL(40)} />
              {isSelf ? (
                <ButtonSmall onClick={() => router.push(user.inAppLink)}>View Profile</ButtonSmall>
              ) : isFollowing ? (
                <ButtonSmall onClick={handleUnfollow}>Following</ButtonSmall>
              ) : (
                <HighContrastButtonSmall onClick={handleFollow}>Follow</HighContrastButtonSmall>
              )}
            </StyledHoverHeader>
            <StyledName>{user.snapshot.displayName}</StyledName>
            <Username user={user} />
          </div>
          {user.snapshot.bio && <StyledBio>{`> ${user.snapshot.bio}`}</StyledBio>}
        </StyledHoverCard>
      )}
      <div ref={innerRef}>{children}</div>
    </div>
  );
};

const StyledHoverCard = styled.div`
  position: absolute;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 230px;
  color: ${({ theme }) => theme.primary};
  background: ${({ theme }) => theme.background};
  border: ${({ theme }) => `2px solid ${theme.primary}`};
  padding: 13px;
  font-family: Inter;
  z-index: ${Layer.WIN_LAYER};
`;

const StyledHoverHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
`;

const StyledName = styled.p`
  font-size: 15px;
  line-height: 18px;
  font-weight: 600;
  margin-top: 2px;
`;

const StyledBio = styled.p`
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  font-family: "IBM Plex Mono";
  margin-top: 10px;
`;
